import React, { useCallback, useEffect, useMemo, useState } from "react";
import { readinessHex } from "../isf/commandCenterModel";
import {
  buildSchedulePreviewReport,
  CHECK_STATUS,
  visitTypeBadgeClass,
} from "./schedulePreviewChecklist";
import { resolveCheckImpact } from "./schedulePreviewImpacts";
import { GROUP_TYPE, SETUP_TOGGLES } from "./scheduleWorkflow";
import { fetchScheduleStudyInfoSummary } from "./scheduleStudyInfoService";
import SchedulePreviewCommHover from "./SchedulePreviewCommHover";

function StatusIcon({ status, small = false }) {
  const cls = `schedule-preview-status${small ? " schedule-preview-status--sm" : ""}`;
  if (status === CHECK_STATUS.COMPLETE) {
    return <i className={`fas fa-check-circle ${cls} is-complete`} aria-hidden />;
  }
  if (status === CHECK_STATUS.BLOCKED) {
    return <i className={`fas fa-exclamation-circle ${cls} is-blocked`} aria-hidden />;
  }
  return <i className={`far fa-circle ${cls} is-pending`} aria-hidden />;
}

function ReadinessRing({ percent = 0, label, color, showLabel = true }) {
  const radius = 46;
  const circumference = 2 * Math.PI * radius;
  const safe = Math.min(100, Math.max(0, Number(percent) || 0));
  const offset = circumference - (safe / 100) * circumference;
  const stroke = color || readinessHex(safe);

  return (
    <div className="schedule-readiness-ring">
      <div className="schedule-readiness-ring__chart">
        <svg className="schedule-readiness-ring__svg" viewBox="0 0 120 120" aria-hidden>
          <circle className="schedule-readiness-ring__track" cx="60" cy="60" r={radius} />
          <circle
            className="schedule-readiness-ring__progress"
            cx="60"
            cy="60"
            r={radius}
            stroke={stroke}
            strokeDasharray={circumference}
            strokeDashoffset={offset}
          />
        </svg>
        <div className="schedule-readiness-ring__value" style={{ color: stroke }}>
          {safe}%
        </div>
      </div>
      {label && showLabel ? <p className="schedule-readiness-ring__label">{label}</p> : null}
    </div>
  );
}

function ChecklistRow({ row, showMessage = true, showValueOnComplete = false }) {
  const isIncomplete =
    row.status === CHECK_STATUS.PENDING || row.status === CHECK_STATUS.BLOCKED;
  const impact = isIncomplete
    ? resolveCheckImpact(row.label, row.status)
    : "";
  const showDetail = row.message && (isIncomplete || showValueOnComplete);

  return (
    <li className={`schedule-preview-check is-${row.status || "pending"}`}>
      <StatusIcon status={row.status} small />
      <div className="schedule-preview-check__body">
        <span className="schedule-preview-check__label">
          {row.label}
          {row.optionalForLive ? (
            <span className="schedule-preview-check__optional">Optional</span>
          ) : null}
        </span>
        {showMessage && showDetail ? (
          row.commTemplates?.length ? (
            <SchedulePreviewCommHover
              summary={row.message}
              templates={row.commTemplates}
              isDetail={!isIncomplete}
            />
          ) : (
            <span
              className={`schedule-preview-check__message${
                !isIncomplete ? " is-detail" : ""
              }`}
            >
              {row.message}
            </span>
          )
        ) : null}
        {showMessage && impact ? (
          <span className="schedule-preview-check__impact">
            {row.optionalForLive ? impact : `If not completed: ${impact}`}
          </span>
        ) : null}
      </div>
    </li>
  );
}

function VisitProgressBar({ percent }) {
  const safe = Math.min(100, Math.max(0, Number(percent) || 0));
  const color = readinessHex(safe);
  return (
    <div className="schedule-readiness-visit__bar" aria-hidden>
      <span
        className="schedule-readiness-visit__bar-fill"
        style={{ width: `${safe}%`, background: color }}
      />
    </div>
  );
}

function VisitTypeBadge({ label, fullLabel, typeKey }) {
  return (
    <span
      className={`schedule-readiness-visit__type-badge ${visitTypeBadgeClass(typeKey)}`}
      title={fullLabel || label}
    >
      {label}
    </span>
  );
}

function VisitReadinessCard({ visit, defaultOpen = false, showValueOnComplete = false }) {
  const completeCount = visit.items.filter(
    (row) => row.status === CHECK_STATUS.COMPLETE,
  ).length;
  const total = visit.items.length || 1;
  const percent = Math.round((completeCount / total) * 100);
  const progressColor = readinessHex(percent);
  const [open, setOpen] = useState(defaultOpen);

  return (
    <article className={`schedule-readiness-visit is-${visit.status}${visit.isAdhoc ? " is-adhoc" : ""}`}>
      <button
        type="button"
        className="schedule-readiness-visit__head"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <StatusIcon status={visit.status} small />
        <VisitTypeBadge
          label={visit.visitTypeBadge || visit.visitType}
          fullLabel={visit.visitType}
          typeKey={visit.visitTypeKey}
        />
        <strong className="schedule-readiness-visit__name" title={visit.visitName}>
          {visit.visitName}
        </strong>
        <div className="schedule-readiness-visit__progress-col">
          <VisitProgressBar percent={percent} />
          <div className="schedule-readiness-visit__progress-meta">
            <span
              className="schedule-readiness-visit__pct"
              style={{ color: progressColor }}
            >
              {percent}%
            </span>
            <span className="schedule-readiness-visit__count">
              {completeCount}/{total} completed
            </span>
          </div>
        </div>
        <i className={`fas fa-chevron-${open ? "up" : "down"}`} aria-hidden />
      </button>
      {open ? (
        <ul className="schedule-preview-checklist schedule-preview-checklist--nested">
          {visit.items.map((row) => (
            <ChecklistRow
              key={`${visit.visitId}-${row.label}`}
              row={row}
              showValueOnComplete={showValueOnComplete}
            />
          ))}
        </ul>
      ) : null}
    </article>
  );
}

function SetupChecklist({ items, onOpenSetup }) {
  const completed = items.filter((row) => row.status === CHECK_STATUS.COMPLETE);
  const remaining = items.filter((row) => row.status !== CHECK_STATUS.COMPLETE);

  return (
    <section className="schedule-readiness-card schedule-readiness-card--setup">
      <div className="schedule-readiness-card__head">
        <h3>Setup &amp; structure</h3>
        <button type="button" className="btn btn-link btn-sm p-0" onClick={onOpenSetup}>
          Open setup
        </button>
      </div>

      {completed.length ? (
        <div className="schedule-readiness-setup-block is-complete">
          <h4>
            <i className="fas fa-check-circle" aria-hidden />
            Completed ({completed.length})
          </h4>
          <ul className="schedule-preview-checklist">
            {completed.map((row) => (
              <ChecklistRow key={row.label} row={row} showMessage={false} />
            ))}
          </ul>
        </div>
      ) : null}

      {remaining.length ? (
        <div className="schedule-readiness-setup-block is-remaining">
          <h4>
            <i className="fas fa-hourglass-half" aria-hidden />
            Remaining ({remaining.length})
          </h4>
          <ul className="schedule-preview-checklist">
            {remaining.map((row) => (
              <ChecklistRow key={row.label} row={row} />
            ))}
          </ul>
        </div>
      ) : null}

      {!completed.length && !remaining.length ? (
        <p className="schedule-readiness-card__empty">No setup checks loaded.</p>
      ) : null}
    </section>
  );
}

function computeGroupReadiness(visits = []) {
  let complete = 0;
  let total = 0;
  visits.forEach((visit) => {
    (visit.items || []).forEach((row) => {
      if (row.status === CHECK_STATUS.SKIPPED) return;
      total += 1;
      if (row.status === CHECK_STATUS.COMPLETE) complete += 1;
    });
  });
  const percent = total ? Math.round((complete / total) * 100) : 0;
  return { complete, total, percent };
}

function GroupReadinessSection({ group, expandAllVisits = false, showValueOnComplete = false }) {
  const { complete, total, percent } = computeGroupReadiness(group.visits);
  const progressColor = readinessHex(percent);

  return (
    <div className="schedule-readiness-group schedule-readiness-group--multi">
      <div className="schedule-readiness-group__head">
        <div className="schedule-readiness-group__title-wrap">
          <h4 className="schedule-readiness-group__title">{group.groupName}</h4>
          <span className="schedule-readiness-group__meta">
            {group.visits.length} visit{group.visits.length === 1 ? "" : "s"}
          </span>
        </div>
        <div className="schedule-readiness-group__progress-col">
          <VisitProgressBar percent={percent} />
          <div className="schedule-readiness-visit__progress-meta">
            <span
              className="schedule-readiness-visit__pct"
              style={{ color: progressColor }}
            >
              {percent}%
            </span>
            <span className="schedule-readiness-visit__count">
              {complete}/{total} completed
            </span>
          </div>
        </div>
      </div>
      {group.visits.length ? (
        group.visits.map((visit, index) => (
          <VisitReadinessCard
            key={visit.visitId || `${visit.visitName}-${index}`}
            visit={visit}
            defaultOpen={expandAllVisits}
            showValueOnComplete={showValueOnComplete}
          />
        ))
      ) : (
        <p className="schedule-readiness-card__empty">No visits in this group.</p>
      )}
    </div>
  );
}

function VisitReadinessRail({
  visitGroups,
  showGroupHeaders,
  totalVisits,
  enabledOptionalLabels,
  onOpenVisits,
  expandAllVisits = false,
  showValueOnComplete = false,
}) {
  return (
    <section className="schedule-readiness-card schedule-readiness-card--visits">
      <div className="schedule-readiness-card__head">
        <h3>
          Readiness by visit
          <span className="schedule-readiness-card__count">({totalVisits})</span>
        </h3>
        {onOpenVisits ? (
          <button type="button" className="btn btn-link btn-sm p-0" onClick={onOpenVisits}>
            Open visits
          </button>
        ) : null}
      </div>
      {enabledOptionalLabels.length ? (
        <p
          className="schedule-readiness-card__note"
          title={`Enabled: ${enabledOptionalLabels.join(", ")}`}
        >
          Enabled: {enabledOptionalLabels.join(", ")}.
        </p>
      ) : null}
      <div
        className={`schedule-readiness-visits__body${
          showGroupHeaders ? " schedule-readiness-visits__body--grouped" : ""
        }`}
      >
        {visitGroups.map((group) =>
          showGroupHeaders ? (
            <GroupReadinessSection
              key={group.groupId || group.groupName}
              group={group}
              expandAllVisits={expandAllVisits}
              showValueOnComplete={showValueOnComplete}
            />
          ) : (
            <div key={group.groupId || group.groupName} className="schedule-readiness-group">
              {group.visits.length ? (
                group.visits.map((visit, index) => (
                  <VisitReadinessCard
                    key={visit.visitId || `${visit.visitName}-${index}`}
                    visit={visit}
                    defaultOpen={expandAllVisits}
                    showValueOnComplete={showValueOnComplete}
                  />
                ))
              ) : (
                <p className="schedule-readiness-card__empty">No visits in this group.</p>
              )}
            </div>
          ),
        )}
      </div>
    </section>
  );
}

function badgeTextColor(background) {
  const raw = String(background || "").trim().replace("#", "");
  if (raw.length < 6) return "#ffffff";
  const r = parseInt(raw.slice(0, 2), 16);
  const g = parseInt(raw.slice(2, 4), 16);
  const b = parseInt(raw.slice(4, 6), 16);
  if ([r, g, b].some((n) => Number.isNaN(n))) return "#ffffff";
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.58 ? "#0f172a" : "#ffffff";
}

function StudyStaffLine({ name, employeeNum }) {
  const cleanName = String(name || "")
    .replace(/\s*\(\d+\)\s*$/, "")
    .trim();
  return (
    <span className="schedule-study-info__staff-line">
      {employeeNum ? (
        <span className="schedule-study-info__staff-num-badge">{employeeNum}</span>
      ) : null}
      <span className="schedule-study-info__staff-name">{cleanName || "—"}</span>
    </span>
  );
}

function StudyInfoCard({ studyInfo, loading }) {
  const studyColor = studyInfo?.studyColor || "#2563eb";
  const studyBadgeLabel = studyInfo
    ? `${String(studyInfo.studyPrefix || "STD").toUpperCase()}-${studyInfo.studyNumber || "—"}`
    : "—";

  return (
    <section className="schedule-readiness-card schedule-readiness-card--study-info">
      <div className="schedule-readiness-card__head">
        <h3>Study info</h3>
      </div>

      {loading ? (
        <p className="schedule-readiness-card__empty">Loading study details…</p>
      ) : !studyInfo ? (
        <p className="schedule-readiness-card__empty">Study details not available.</p>
      ) : (
        <div className="schedule-study-info">
          <div className="schedule-study-info__study-line">
            <span
              className="schedule-study-info__study-badge"
              style={{
                backgroundColor: studyColor,
                color: badgeTextColor(studyColor),
              }}
              title="Study number"
            >
              {studyBadgeLabel}
            </span>
            {studyInfo.studyName ? (
              <span className="schedule-study-info__study-title" title={studyInfo.studyName}>
                {studyInfo.studyName}
              </span>
            ) : null}
          </div>

          <div className="schedule-study-info__block">
            <span className="schedule-study-info__label">Principal Investigator</span>
            <StudyStaffLine
              name={studyInfo.piName || "—"}
              employeeNum={studyInfo.piEmployeeNum}
            />
          </div>

          <div className="schedule-study-info__block">
            <span className="schedule-study-info__label">
              Sites
              {studyInfo.sites?.length ? (
                <span className="schedule-study-info__count">({studyInfo.sites.length})</span>
              ) : null}
            </span>
            {studyInfo.sites?.length ? (
              <ul className="schedule-study-info__site-list">
                {studyInfo.sites.map((site) => (
                  <li key={site.id} className="schedule-study-info__site-item">
                    {site.name}
                  </li>
                ))}
              </ul>
            ) : (
              <span className="schedule-study-info__empty">No sites mapped</span>
            )}
          </div>

          <div className="schedule-study-info__block schedule-study-info__block--users">
            <span className="schedule-study-info__label">
              Assigned staff
              {studyInfo.assignedUsers?.length ? (
                <span className="schedule-study-info__count">
                  ({studyInfo.assignedUsers.length})
                </span>
              ) : null}
            </span>
            {studyInfo.assignedUsers?.length ? (
              <ul className="schedule-study-info__staff-list">
                {studyInfo.assignedUsers.map((user, index) => (
                  <li key={`${user.id || user.name}-${index}`}>
                    <StudyStaffLine name={user.name} employeeNum={user.employeeNum} />
                  </li>
                ))}
              </ul>
            ) : (
              <span className="schedule-study-info__empty">No assigned staff</span>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

export default function SchedulePreviewPanel({
  wizard,
  cohort,
  scheduleCreated,
  isScheduleLive,
  visitBuilderMode,
  visitCohorts,
  studyScheduleId,
  organizationId,
  createdBy,
  trialStudyId,
  studyFromNav = null,
  onGoToStep,
  embedded = false,
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [report, setReport] = useState(null);
  const [studyInfo, setStudyInfo] = useState(null);
  const [studyInfoLoading, setStudyInfoLoading] = useState(true);

  const enabledOptionalLabels = useMemo(
    () =>
      SETUP_TOGGLES.filter((toggle) => {
        if (toggle.dependsOn && !wizard.setupFlags[toggle.dependsOn]) return false;
        return !!wizard.setupFlags[toggle.key];
      }).map((toggle) => toggle.label),
    [wizard.setupFlags],
  );

  const showGroupHeaders =
    wizard.groupType === GROUP_TYPE.MULTIPLE && visitBuilderMode === "cohorts";

  const loadReport = useCallback(async () => {
    if (!studyScheduleId || !organizationId) {
      setReport(null);
      setError("Schedule is not ready yet. Complete Setup first.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const next = await buildSchedulePreviewReport({
        wizard,
        cohort,
        scheduleCreated,
        visitBuilderMode,
        visitCohorts,
        studyScheduleId,
        organizationId,
        createdBy,
      });
      setReport(next);
    } catch (err) {
      setReport(null);
      setError(err.message || "Could not build schedule preview.");
    } finally {
      setLoading(false);
    }
  }, [
    cohort,
    createdBy,
    organizationId,
    scheduleCreated,
    studyScheduleId,
    visitBuilderMode,
    visitCohorts,
    wizard,
  ]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const loadStudyInfo = useCallback(async () => {
    setStudyInfoLoading(true);
    try {
      const summary = await fetchScheduleStudyInfoSummary({
        studyId: trialStudyId,
        organizationId,
        staffId: createdBy,
        studyFromNav,
        cohort,
      });
      setStudyInfo(summary);
    } catch {
      setStudyInfo(null);
    } finally {
      setStudyInfoLoading(false);
    }
  }, [cohort, createdBy, organizationId, studyFromNav, trialStudyId]);

  useEffect(() => {
    loadStudyInfo();
  }, [loadStudyInfo]);

  const progressPct = report?.summary?.total
    ? Math.round((report.summary.complete / report.summary.total) * 100)
    : 0;

  const setupItems = report
    ? [...report.setupItems, ...report.visitStructureItems]
    : [];

  const openSetupStep = () =>
    onGoToStep?.(
      wizard.groupType === GROUP_TYPE.MULTIPLE ? "groups-visits" : "setup",
    );

  return (
    <div
      className={`schedule-preview-panel${
        embedded ? " schedule-preview-panel--embedded" : ""
      }`}
    >
      {!embedded ? (
        <div className="schedule-preview-panel__intro">
          <div>
            <h2 className="schedule-preview-panel__title">Schedule readiness</h2>
            <p className="schedule-preview-panel__desc">
              {isScheduleLive
                ? "Review completion before updating the live schedule."
                : "Review what is done and what remains before going live."}
            </p>
          </div>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            disabled={loading}
            onClick={loadReport}
          >
            <i className={`fas fa-sync-alt${loading ? " fa-spin" : ""}`} aria-hidden />
            Refresh
          </button>
        </div>
      ) : null}

      {loading ? (
        <div className="schedule-preview-panel__loading">
          <div className="spinner-border spinner-border-sm text-primary" role="status">
            <span className="visually-hidden">Checking schedule…</span>
          </div>
          <span>Checking saved setup and visit data…</span>
        </div>
      ) : null}

      {!loading && error ? (
        <div className="schedule-preview-panel__error">{error}</div>
      ) : null}

      {!loading && report ? (
        <div className="schedule-readiness-dashboard">
          <div className="schedule-readiness-dashboard__col-start">
            <section className="schedule-readiness-card schedule-readiness-card--summary">
              <div className="schedule-readiness-card__head">
                <h3>Readiness check</h3>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  disabled={loading}
                  onClick={loadReport}
                  title="Refresh readiness checks"
                >
                  <i className={`fas fa-sync-alt${loading ? " fa-spin" : ""}`} aria-hidden />
                </button>
              </div>

              <div className="schedule-readiness-summary__body">
                <ReadinessRing
                  percent={progressPct}
                  label="Overall readiness"
                  color={readinessHex(progressPct)}
                  showLabel={false}
                />

                <div className="schedule-readiness-summary__side">
                  <p className="schedule-readiness-summary__side-label">Overall readiness</p>

                  <div className="schedule-readiness-stats">
                    <div className="schedule-readiness-stats__row">
                      <span>Checks complete</span>
                      <strong>
                        {report.summary.complete}/{report.summary.total}
                      </strong>
                    </div>
                    <div className="schedule-readiness-stats__row">
                      <span>Visits</span>
                      <strong>{report.visitReports.length}</strong>
                    </div>
                    {showGroupHeaders ? (
                      <div className="schedule-readiness-stats__row">
                        <span>Groups</span>
                        <strong>{report.visitGroups.length}</strong>
                      </div>
                    ) : null}
                  </div>

                  <div className="schedule-preview-summary__chips">
                    <span className="schedule-preview-chip is-complete">
                      {report.summary.complete} done
                    </span>
                    {report.summary.pending ? (
                      <span className="schedule-preview-chip is-pending">
                        {report.summary.pending} left
                      </span>
                    ) : null}
                    {report.summary.blocked ? (
                      <span className="schedule-preview-chip is-blocked">
                        {report.summary.blocked} blocked
                      </span>
                    ) : null}
                  </div>

                  <div
                    className={`schedule-preview-readiness${
                      report.summary.readyForLive ? " is-ready" : " is-pending"
                    }`}
                  >
                    {report.summary.readyForLive ? (
                      <>
                        <i className="fas fa-check-circle" aria-hidden />
                        {report.summary.optionalPending
                          ? "Ready to publish. Documents and adhoc setup are optional."
                          : "Ready to publish."}
                      </>
                    ) : (
                      <>
                        <i className="fas fa-info-circle" aria-hidden />
                        Complete remaining required items before publishing.
                      </>
                    )}
                  </div>
                </div>
              </div>
            </section>

            <StudyInfoCard studyInfo={studyInfo} loading={studyInfoLoading} />
          </div>

          <SetupChecklist items={setupItems} onOpenSetup={openSetupStep} />

          <VisitReadinessRail
            visitGroups={report.visitGroups}
            showGroupHeaders={showGroupHeaders}
            totalVisits={report.visitReports.length}
            enabledOptionalLabels={enabledOptionalLabels}
            onOpenVisits={() =>
              onGoToStep?.(
                wizard.groupType === GROUP_TYPE.MULTIPLE ? "groups-visits" : "visits",
              )
            }
          />
        </div>
      ) : null}
    </div>
  );
}

export function SchedulePreviewVisitRail({
  visitGroups = [],
  showGroupHeaders = false,
  totalVisits = 0,
  enabledOptionalLabels = [],
  expandAllVisits = false,
  showValueOnComplete = false,
}) {
  return (
    <VisitReadinessRail
      visitGroups={visitGroups}
      showGroupHeaders={showGroupHeaders}
      totalVisits={totalVisits}
      enabledOptionalLabels={enabledOptionalLabels}
      expandAllVisits={expandAllVisits}
      showValueOnComplete={showValueOnComplete}
    />
  );
}
